// Martian Console -- INTELLIGENCE navigation (single source of truth).
//
// Phase 1 reduced the brand console to six destinations and left the
// specialist intelligence/data surfaces without any entry point (the
// transformation map said they "remain one level down", but nothing linked
// to them). This module restores that one level down WITHOUT a second
// navigation system: it is a pure data list consumed by both existing
// shells -- the founder rail (FounderShell) and the legacy sidebar
// (DashboardLayout, which every specialist page below still renders in).
//
// Rules for what belongs here:
//   - every `to` is a registered route in App.js that a brand tenant can
//     actually open (no BrandRedirect that bounces it elsewhere, no
//     PersonaGuard that excludes brand);
//   - every page reads real data (existing connector / DataForSEO / AI
//     execution pipelines). Pages whose own source declares seeded or
//     fabricated data (/seo, /ads, /ai-traffic, /marketplace) are NOT listed;
//   - labels are the names the pages already use. Nothing is invented.
//
// Not listed on purpose:
//   Social  -- no social page exists; social accounts are managed under
//              Evidence -> Connections.
//   AI Ads  -- /ads is seeded data (FabricatedDataNotice); real ad-platform
//              data is the Paid Media page below.
//   AI Visibility (/ai-discovery) -- Phase 1 redirects it to the Briefing
//              for brand tenants, so a link here would be a loop.

export const INTELLIGENCE_GROUPS = [
  {
    key: 'ai-search',
    label: 'AI Search',
    items: [
      { key: 'search-everywhere', to: '/search-everywhere', label: 'AI Answers & Mentions' },
      { key: 'som', to: '/som', label: 'Share of Model', freePlanOpen: true },
      { key: 'geo-report', to: '/geo-report', label: 'Web Search Visibility' },
      { key: 'ai-raw-data', to: '/ai-discovery/raw-data', label: 'AI Research Data' },
    ],
  },
  {
    key: 'search',
    label: 'Search & SEO',
    items: [
      { key: 'data-search', to: '/data/search', label: 'Search' },
      { key: 'search-demand', to: '/search-demand', label: 'Search Console' },
      { key: 'analytics', to: '/analytics', label: 'Analytics' },
    ],
  },
  {
    key: 'commerce',
    label: 'Commerce',
    items: [{ key: 'data-ecommerce', to: '/growth/ecommerce', label: 'Ecommerce' }],
  },
  {
    key: 'paid',
    label: 'Paid Growth',
    items: [{ key: 'data-paid-media', to: '/growth/paid-media', label: 'Paid Media' }],
  },
  {
    key: 'creative',
    label: 'Creative',
    // Creative Intelligence lives inside Work -> Create for brand tenants
    // (/creative-intelligence redirects there).
    items: [{ key: 'intel-creative', to: '/work/create', label: 'Creative Intelligence' }],
  },
  {
    key: 'market',
    label: 'Market',
    items: [
      { key: 'data-entities', to: '/data/entities', label: 'Entities' },
      { key: 'growth-environment', to: '/growth/environment', label: 'Environment Intelligence' },
    ],
  },
];

export const INTELLIGENCE_ITEMS = INTELLIGENCE_GROUPS.flatMap((g) => g.items);

export const intelligenceItemActive = (item, pathname) =>
  pathname === item.to || pathname.startsWith(`${item.to}/`);

export const isIntelligencePath = (pathname) =>
  INTELLIGENCE_ITEMS.some((item) => intelligenceItemActive(item, pathname));

// ---- Free Evaluation Workspace locks -------------------------------------
// A free-plan brand tenant still SEES the whole tree (show what you'll
// unlock, don't hide it); the destinations the plan gates carry the
// established lock icon and explain themselves instead of navigating.
//
// This module deliberately holds NO list of locked routes. The entitlement
// source of truth is FREE_PLAN_LOCKED_PATHS (DashboardLayout.js, derived from
// the brandFree nav config and enforced by App.js's PersonaGuard); callers
// pass it in. The predicate mirrors PersonaGuard's own condition exactly, so
// a lock icon is shown if and only if a click-through would be blocked.
export const LOCKED_NAV_TOAST = 'This unlocks in your full Martian workspace';

export const isFreePlanBrand = (user) =>
  (user?.tenant_type || 'brand') === 'brand' && user?.plan === 'free';

// Tenant-scoped product audit grant from GET /auth/me (server-validated;
// never a frontend-only switch). `intelligence` opens the founder
// Intelligence destinations below without changing plan and without
// unlocking Creator or the growth pipeline.
export const hasIntelligenceAuditAccess = (user) =>
  Array.isArray(user?.product_audit_scopes) && user.product_audit_scopes.includes('intelligence');

export const intelligenceAuditPath = (pathname) =>
  INTELLIGENCE_ITEMS.some((item) => item.to === pathname && !item.freePlanOpen);

// One predicate for sidebar lock icons and PersonaGuard URL blocks.
export const isFreePlanPathLocked = (pathname, user, lockedPaths) => {
  if (!isFreePlanBrand(user) || !Array.isArray(lockedPaths) || !lockedPaths.includes(pathname)) return false;
  if (hasIntelligenceAuditAccess(user) && intelligenceAuditPath(pathname)) return false;
  return true;
};

// `freePlanOpen` marks a destination whose route has no PersonaGuard, so a
// free tenant genuinely opens it even if a legacy sidebar entry still
// listed it as locked. It is not a lock list -- it only stops a lock icon
// from claiming a block that does not exist. intelligenceLocks.test.js pins
// it against App.js so it can never drift from the real route enforcement.
export const isIntelligenceItemLocked = (item, user, lockedPaths) =>
  !item.freePlanOpen && isFreePlanPathLocked(item.to, user, lockedPaths);
